import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNotifications } from './NotificationContext';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

// Tipos
export interface Event {
  id: number;
  title: string;
  date: Date;
  time: string;
  client: string;
  clientId?: number;
  type: 'visita' | 'contrato' | 'avaliacao' | 'reuniao' | 'outro';
  status: 'agendado' | 'confirmado' | 'concluido' | 'cancelado';
  location?: string;
  description?: string;
  propertyId?: number;
  responsibleId?: number;
  createdAt: Date;
  updatedAt: Date;
  notes?: string;
  isNew?: boolean; // Para controlar notificações
}

// Chave para armazenar eventos no localStorage
const EVENTS_STORAGE_KEY = 'events';
const NEW_EVENTS_COUNT_KEY = 'newEventsCount';

// Dados de exemplo
const initialEvents: Event[] = [
  { 
    id: 1, 
    title: 'Visita ao imóvel', 
    date: new Date(2025, 3, 8), 
    time: '14:00', 
    client: 'João Silva', 
    clientId: 101,
    type: 'visita', 
    status: 'agendado',
    location: 'Rua das Flores, 123',
    description: 'Visita ao apartamento de 3 quartos no centro',
    propertyId: 201,
    responsibleId: 301,
    createdAt: new Date(2025, 3, 5),
    updatedAt: new Date(2025, 3, 5),
    notes: 'Cliente interessado em financiamento'
  },
  { 
    id: 2, 
    title: 'Assinatura de contrato', 
    date: new Date(2025, 3, 8), 
    time: '16:30', 
    client: 'Maria Santos', 
    clientId: 102,
    type: 'contrato', 
    status: 'confirmado',
    location: 'Escritório central',
    description: 'Assinatura de contrato de venda da casa',
    propertyId: 202,
    responsibleId: 302,
    createdAt: new Date(2025, 3, 4),
    updatedAt: new Date(2025, 3, 6),
    notes: 'Trazer documentação completa'
  },
  { 
    id: 3, 
    title: 'Avaliação de imóvel', 
    date: new Date(2025, 3, 9), 
    time: '10:00', 
    client: 'Pedro Oliveira', 
    clientId: 103,
    type: 'avaliacao', 
    status: 'agendado',
    location: 'Av. Principal, 456',
    description: 'Avaliação para venda de casa',
    propertyId: 203,
    responsibleId: 303,
    createdAt: new Date(2025, 3, 3),
    updatedAt: new Date(2025, 3, 3),
    notes: 'Levar material fotográfico'
  },
  { 
    id: 4, 
    title: 'Reunião com investidor', 
    date: new Date(2025, 3, 10), 
    time: '11:30', 
    client: 'Carlos Mendes', 
    clientId: 104,
    type: 'reuniao', 
    status: 'agendado',
    location: 'Sala de reuniões',
    description: 'Apresentação de oportunidades de investimento',
    propertyId: undefined,
    responsibleId: 304,
    createdAt: new Date(2025, 3, 2),
    updatedAt: new Date(2025, 3, 2),
    notes: 'Preparar apresentação de portfólio'
  },
  { 
    id: 5, 
    title: 'Segunda visita ao apartamento', 
    date: new Date(2025, 3, 11), 
    time: '15:00', 
    client: 'Ana Pereira', 
    clientId: 105,
    type: 'visita', 
    status: 'agendado',
    location: 'Condomínio Parque Verde, Bloco B, Apto 302',
    description: 'Segunda visita para medições',
    propertyId: 205,
    responsibleId: 305,
    createdAt: new Date(2025, 3, 1),
    updatedAt: new Date(2025, 3, 1),
    notes: 'Cliente virá com arquiteto'
  }
];

// Funções auxiliares para gerenciar eventos no localStorage
const loadEvents = (): Event[] => {
  try {
    const storedEvents = localStorage.getItem(EVENTS_STORAGE_KEY);
    if (storedEvents) {
      const parsedEvents = JSON.parse(storedEvents);
      // Converter strings de data para objetos Date
      return parsedEvents.map((event: any) => ({
        ...event,
        date: new Date(event.date),
        createdAt: new Date(event.createdAt),
        updatedAt: new Date(event.updatedAt)
      }));
    }
    // Se não existirem eventos armazenados, usar os eventos iniciais
    localStorage.setItem(EVENTS_STORAGE_KEY, JSON.stringify(initialEvents));
    return initialEvents;
  } catch (error) {
    console.error('Erro ao carregar eventos:', error);
    return initialEvents;
  }
};

const saveEvents = (events: Event[]): void => {
  try {
    localStorage.setItem(EVENTS_STORAGE_KEY, JSON.stringify(events));
  } catch (error) {
    console.error('Erro ao salvar eventos:', error);
  }
};

const loadNewEventsCount = (): number => {
  try {
    const count = localStorage.getItem(NEW_EVENTS_COUNT_KEY);
    return count ? parseInt(count, 10) : 0;
  } catch (error) {
    console.error('Erro ao carregar contagem de novos eventos:', error);
    return 0;
  }
};

const saveNewEventsCount = (count: number): void => {
  try {
    localStorage.setItem(NEW_EVENTS_COUNT_KEY, count.toString());
  } catch (error) {
    console.error('Erro ao salvar contagem de novos eventos:', error);
  }
};

// Interface do contexto
interface EventContextData {
  events: Event[];
  newEventsCount: number;
  loading: boolean;
  addEvent: (event: Omit<Event, 'id' | 'createdAt' | 'updatedAt' | 'isNew'>) => void;
  updateEvent: (event: Event) => void;
  deleteEvent: (id: number) => void;
  markAllEventsAsRead: () => void;
  getUpcomingEvents: (limit?: number) => Event[];
  getTodayEvents: () => Event[];
  getEventsByDate: (date: Date) => Event[];
  getEventsByType: (type: Event['type']) => Event[];
  getEventsByStatus: (status: Event['status']) => Event[];
}

// Criação do contexto
const EventContext = createContext<EventContextData>({} as EventContextData);

// Provider do contexto
export const EventProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [newEventsCount, setNewEventsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { createNotification } = useNotifications();

  // Carregar eventos ao iniciar
  useEffect(() => {
    const loadedEvents = loadEvents();
    setEvents(loadedEvents);
    setNewEventsCount(loadNewEventsCount());
    setLoading(false);
  }, []);

  // Adicionar um novo evento
  const addEvent = useCallback((eventData: Omit<Event, 'id' | 'createdAt' | 'updatedAt' | 'isNew'>) => {
    const now = new Date();
    const newEvent: Event = {
      ...eventData,
      id: Math.max(...events.map(e => e.id), 0) + 1,
      createdAt: now,
      updatedAt: now,
      isNew: true
    };

    const updatedEvents = [...events, newEvent];
    setEvents(updatedEvents);
    saveEvents(updatedEvents);
    
    // Incrementar contador de novos eventos
    const newCount = newEventsCount + 1;
    setNewEventsCount(newCount);
    saveNewEventsCount(newCount);

    // Criar notificação para o novo evento
    createNotification({
      title: 'Novo Evento',
      message: `${newEvent.title} - ${format(newEvent.date, 'dd/MM/yyyy', { locale: ptBR })} às ${newEvent.time}`,
      type: 'info',
      link: '/eventos',
      linkText: 'Ver Detalhes'
    });

    return newEvent;
  }, [events, newEventsCount, createNotification]);

  // Atualizar um evento existente
  const updateEvent = useCallback((updatedEvent: Event) => {
    const updatedEvents = events.map(event => 
      event.id === updatedEvent.id 
        ? { ...updatedEvent, updatedAt: new Date() } 
        : event
    );
    
    setEvents(updatedEvents);
    saveEvents(updatedEvents);

    // Criar notificação para o evento atualizado
    createNotification({
      title: 'Evento Atualizado',
      message: `${updatedEvent.title} - ${format(updatedEvent.date, 'dd/MM/yyyy', { locale: ptBR })} às ${updatedEvent.time}`,
      type: 'info',
      link: '/eventos',
      linkText: 'Ver Detalhes'
    });
  }, [events, createNotification]);

  // Excluir um evento
  const deleteEvent = useCallback((id: number) => {
    const eventToDelete = events.find(event => event.id === id);
    if (!eventToDelete) return;

    const updatedEvents = events.filter(event => event.id !== id);
    setEvents(updatedEvents);
    saveEvents(updatedEvents);

    // Criar notificação para o evento excluído
    createNotification({
      title: 'Evento Excluído',
      message: `${eventToDelete.title} foi removido do calendário`,
      type: 'warning'
    });
  }, [events, createNotification]);

  // Marcar todos os eventos como lidos (remover flag isNew)
  const markAllEventsAsRead = useCallback(() => {
    if (newEventsCount === 0) return;

    const updatedEvents = events.map(event => ({
      ...event,
      isNew: false
    }));

    setEvents(updatedEvents);
    saveEvents(updatedEvents);
    setNewEventsCount(0);
    saveNewEventsCount(0);
  }, [events, newEventsCount]);

  // Obter próximos eventos
  const getUpcomingEvents = useCallback((limit = 5) => {
    const now = new Date();
    return events
      .filter(event => {
        const eventDate = new Date(event.date);
        eventDate.setHours(parseInt(event.time.split(':')[0]), parseInt(event.time.split(':')[1]));
        return eventDate >= now && event.status !== 'cancelado';
      })
      .sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        dateA.setHours(parseInt(a.time.split(':')[0]), parseInt(a.time.split(':')[1]));
        dateB.setHours(parseInt(b.time.split(':')[0]), parseInt(b.time.split(':')[1]));
        return dateA.getTime() - dateB.getTime();
      })
      .slice(0, limit);
  }, [events]);

  // Obter eventos do dia atual
  const getTodayEvents = useCallback(() => {
    const today = new Date();
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return (
        eventDate.getDate() === today.getDate() &&
        eventDate.getMonth() === today.getMonth() &&
        eventDate.getFullYear() === today.getFullYear()
      );
    }).sort((a, b) => {
      const timeA = a.time.split(':').map(Number);
      const timeB = b.time.split(':').map(Number);
      return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
    });
  }, [events]);

  // Obter eventos por data
  const getEventsByDate = useCallback((date: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      );
    }).sort((a, b) => {
      const timeA = a.time.split(':').map(Number);
      const timeB = b.time.split(':').map(Number);
      return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
    });
  }, [events]);

  // Obter eventos por tipo
  const getEventsByType = useCallback((type: Event['type']) => {
    return events.filter(event => event.type === type);
  }, [events]);

  // Obter eventos por status
  const getEventsByStatus = useCallback((status: Event['status']) => {
    return events.filter(event => event.status === status);
  }, [events]);

  return (
    <EventContext.Provider
      value={{
        events,
        newEventsCount,
        loading,
        addEvent,
        updateEvent,
        deleteEvent,
        markAllEventsAsRead,
        getUpcomingEvents,
        getTodayEvents,
        getEventsByDate,
        getEventsByType,
        getEventsByStatus
      }}
    >
      {children}
    </EventContext.Provider>
  );
};

// Hook para usar o contexto
export const useEvents = () => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvents deve ser usado dentro de um EventProvider');
  }
  return context;
};

export default EventContext;
